import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sparkles, Loader2, Paperclip, Trophy, Lock, Medal,
  FileText, MessageSquare, CheckCircle2, Repeat, ArrowRightLeft,
} from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { useIssuePanel } from "../contexts/IssuePanelContext.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Pagination } from "../components/ui/pagination.js";
import { usePageBack } from "../context/PageBackContext.js";
import { usePaged } from "../lib/usePaged.js";
import { SelectField } from "../components/ui/select-field.js";
import { DropdownButton, DropdownPanel, DropdownCheckboxRow } from "../components/DropdownButton.js";
import { MenuList } from "../components/ui/menu-list.js";
import { Badge } from "../components/ui/badge.js";
import { Textarea } from "../components/ui/textarea.js";
import { IssueTriagePanel, IssueLinks, IssueAutoCaptured, type IssueAssignee, type TriageIssue } from "../components/IssueTriagePanel.js";
import { MODULE_LABELS, moduleLabel } from "../lib/moduleRoutes.js";
import { ISSUE_REOPENABLE, ISSUE_STATUS_ORDER, ISSUE_STATUS_TONES, issueStatusLabel } from "../lib/issueStatus.js";
import { HR_ROLES } from "@hrms/types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Issue {
  id: string; displayId: number; displayIdFormatted: string;
  title: string; type: string; module: string | null; priority: string; status: string;
  description: string | null;
  reporter: string; reporterName: string | null;
  assignee: string | null; assigneeName: string | null; assignedAt?: string | null;
  closedAt: string | null; createdAt: string; updatedAt: string;
  statusReason?: string | null; deferredUntil?: string | null;
  pageUrl?: string | null; browser?: string | null; os?: string | null; viewport?: string | null;
  appVersion?: string | null; userAgent?: string | null; consoleErrors?: string[] | null;
  _count?: { entries: number; attachments: number };
}

interface IssueEntry {
  id: string; issueId: string; entryType: string;
  author: string; authorName: string | null;
  body: string | null; metadataJson: string | null; createdAt: string;
  attachments?: Attachment[];
}

interface Attachment {
  id: string; issueId: string; entryId: string | null;
  filename: string; s3Key: string; mimeType: string | null; sizeBytes: number | null;
  uploadedBy: string; createdAt: string;
}

interface IssueDetail extends Issue, TriageIssue {
  entries: IssueEntry[]; attachments: Attachment[];
}

interface ReporterStats {
  reporter: string; submittedCount: number; confirmedFixedCount: number; verifiedClosedCount: number;
  weightedScore: number; badgesJson: string | null; updatedAt: string;
  displayName: string;
}

interface CreditedIssueItem {
  id: string; displayIdFormatted: string; title: string; priority: string;
  reporterName: string | null; closedAt: string | null;
  otherPartyName: string; otherPartyRole: "resolved by" | "closed by";
}

interface MyImpact {
  submittedCount: number; confirmedFixedCount: number; verifiedClosedCount: number; weightedScore: number;
  badgesJson: string; openCount: number; pendingRetest: number; avgTurnaroundHours: number | null;
}

interface DraftReview {
  suggested_title: string; rewritten_description: string; clarifying_questions: string[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function decodeUser(token: string): { sub: string; email: string; role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { sub: String(p["sub"] ?? ""), email: String(p["email"] ?? ""), role: String(p["role"] ?? "") };
  } catch { return { sub: "", email: "", role: "" }; }
}

const PRIORITY_COLORS: Record<string, string> = {
  low: "var(--color-text-secondary)", medium: "var(--color-warning-accent)", high: "var(--color-error-accent)", critical: "var(--color-accent-accent)",
};
const TYPE_LABELS: Record<string, string> = {
  bug: "Bug", issue: "Issue", feature_request: "Feature Request",
};
const ENTRY_ICONS: Record<string, React.ReactNode> = {
  description: <FileText size={14} />, comment: <MessageSquare size={14} />, resolution: <CheckCircle2 size={14} />,
  retest_result: <Repeat size={14} />, status_change: <ArrowRightLeft size={14} />,
};
const BADGE_LABELS: Record<string, string> = {
  first_report: "First Report", "5_reports": "5 Reports", "10_reports": "10 Reports",
  first_fix: "First Fix", "5_confirmed_fixes": "5 Fixes", "10_confirmed_fixes": "10 Fixes",
  high_impact: "High Impact", found_critical: "Found Critical",
  first_verify: "First Verify", "5_verifies": "5 Verifies", "10_verifies": "10 Verifies",
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor(diff / 60000);
  if (d > 1) return `${d}d ago`;
  if (d === 1) return "yesterday";
  if (h >= 1) return `${h}h ago`;
  if (m >= 1) return `${m}m ago`;
  return "just now";
}

// ── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ label, color }: { label: string; color?: string | undefined }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: "999px",
      fontSize: "11px", fontWeight: 600, background: (color ?? "var(--color-text-secondary)") + "1a",
      color: color ?? "var(--color-text-secondary)", whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

// ── Success Burst ────────────────────────────────────────────────────────────

// Discrete lookup, not inline style -- Tailwind's static scanner only picks up
// class strings that appear literally in source, so the per-particle color is
// chosen by indexing into a fixed set of complete class names, not interpolated.
const BURST_PARTICLE_CLASSES = [
  "bg-[var(--color-success-accent)]", "bg-[var(--color-info-accent)]",
  "bg-[var(--color-accent-accent)]", "bg-[var(--color-warning-accent)]",
];

function SuccessBurst({ show }: { show: boolean }) {
  if (!show) return null;
  const particleCount = 14;
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const angle = (i / particleCount) * 2 * Math.PI + (Math.random() * 0.4 - 0.2);
    const distance = 70 + Math.random() * 60;
    const size = 5 + Math.random() * 5;
    return {
      tx: `${Math.cos(angle) * distance}px`,
      ty: `${Math.sin(angle) * distance}px`,
      delay: `${Math.random() * 100}ms`,
      size,
      colorClass: BURST_PARTICLE_CLASSES[i % BURST_PARTICLE_CLASSES.length]!,
    };
  });

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center">
      <span className="success-burst-ring absolute h-24 w-24 rounded-full border-2 border-[var(--color-success-accent)]" />
      <span className="success-burst-ring absolute h-24 w-24 rounded-full border-2 border-[var(--color-success-accent)] [animation-delay:150ms]" />
      {particles.map((p, i) => (
        <span
          key={i}
          className={`success-burst-particle absolute rounded-full ${p.colorClass}`}
          // width/height/delay are randomized per particle at render time, so
          // unlike the color they genuinely can't be a static Tailwind class.
          style={{ "--tx": p.tx, "--ty": p.ty, width: p.size, height: p.size, animationDelay: p.delay } as React.CSSProperties}
        />
      ))}
      <span className="success-burst-badge relative flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-success-accent)] shadow-[var(--shadow-xl)]">
        <CheckCircle2 className="text-white" size={40} strokeWidth={2.5} />
      </span>
    </div>
  );
}

// ── Issue Detail Drawer ───────────────────────────────────────────────────────

interface DetailDrawerProps {
  issueId: string;
  currentUserId: string;
  canTriage: boolean;
  assignees: IssueAssignee[];
  onClose: () => void;
  onUpdated: (updated: Issue) => void;
  onOpenIssue: (issueId: string) => void;
}

function DetailDrawer({ issueId, currentUserId, canTriage, assignees, onClose, onUpdated, onOpenIssue }: DetailDrawerProps) {
  const [issue, setIssue] = useState<IssueDetail | null>(null);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [retestNote, setRetestNote] = useState("");
  const [resolveSummary, setResolveSummary] = useState("");
  const [resolveTestSteps, setResolveTestSteps] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [attachUrls, setAttachUrls] = useState<Record<string, string>>({});
  const [review, setReview] = useState<DraftReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewAccepted, setReviewAccepted] = useState(false);
  const [reviewError, setReviewError] = useState(false);

  async function load() {
    try {
      const { data } = await api.get<IssueDetail>(`/issues/${issueId}`);
      setIssue(data);
    } catch (err) { logError("IssueTrackerPage.DetailDrawer.load", err); }
  }

  useEffect(() => { load(); }, [issueId]);

  async function loadAttachUrl(att: Attachment) {
    if (attachUrls[att.id]) return;
    try {
      const { data } = await api.get<{ url: string }>(`/issues/${issueId}/attachments/${att.id}/url`);
      setAttachUrls(prev => ({ ...prev, [att.id]: data.url }));
    } catch (err) { logError("IssueTrackerPage.DetailDrawer.loadAttachUrl", err); }
  }

  useEffect(() => {
    if (issue?.attachments) {
      for (const att of issue.attachments) loadAttachUrl(att);
    }
  }, [issue?.attachments]);

  async function doAction(action: string, body?: object) {
    setActionLoading(action);
    try {
      await api.post(`/issues/${issueId}/${action}`, body ?? {});
      await load();
      if (action === "close") {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 1500);
      }
      onUpdated(issue!);
    } catch (err) {
      logError("IssueTrackerPage.DetailDrawer.doAction", err);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }

  async function submitComment() {
    if (!commentText.trim()) throw new Error("validation");
    setCommentLoading(true);
    try {
      await api.post(`/issues/${issueId}/comment`, { note: commentText });
      setCommentText("");
      await load();
    } catch (err) {
      logError("IssueTrackerPage.DetailDrawer.submitComment", err);
      throw err;
    } finally {
      setCommentLoading(false);
    }
  }

  async function handleImprove() {
    if (!issue) return;
    setReviewLoading(true); setReview(null); setReviewError(false);
    try {
      const { data } = await api.post<DraftReview>("/issues/draft-review", {
        title: issue.title, type: issue.type, module: issue.module ?? undefined,
        description: issue.entries.find(e => e.entryType === "description")?.body ?? "",
      });
      setReview(data);
    } catch (err) { logError("IssueTrackerPage.DetailDrawer.handleImprove", err); setReviewError(true); }
    setReviewLoading(false);
  }

  async function acceptImprove() {
    if (!issue || !review) return;
    try {
      await api.patch(`/issues/${issueId}`, { title: review.suggested_title, description: review.rewritten_description });
      setReviewAccepted(true); setReview(null);
      await load();
      onUpdated(issue);
    } catch (err) { logError("IssueTrackerPage.DetailDrawer.acceptImprove", err); }
  }

  if (!issue) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 500 }} onClick={onClose}>
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: "min(640px, 100vw)",
          background: "var(--color-surface-0)", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ color: "var(--color-text-muted)" }}>Loading…</span>
        </div>
      </div>
    );
  }

  const isReporter = issue.reporter === currentUserId;
  const canAct = isReporter && ["resolved", "retesting"].includes(issue.status);
  const canClaim = canTriage && !issue.assignee && ["open", "reopened"].includes(issue.status);
  const canResolve = canTriage && issue.assignee === currentUserId && ["open", "in_progress", "reopened", "needs_info", "deferred"].includes(issue.status);
  // Closed by triage (deferred / not a bug / duplicate / won't fix): the reporter can push back.
  const canContest = (isReporter || canTriage) && ISSUE_REOPENABLE.includes(issue.status) && !["resolved", "retesting", "closed"].includes(issue.status);
  const refresh = async () => { await load(); onUpdated(issue); };
  const metaJson = (entry: IssueEntry) => {
    try { return JSON.parse(entry.metadataJson ?? "{}") as Record<string, string>; }
    catch { return {}; }
  };

  return (
    <>
      <SuccessBurst show={showCelebration} />
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 500 }} onClick={onClose} />
      <div style={{
        position: "fixed", right: 0, top: 0, bottom: 0, zIndex: 501,
        width: "min(640px, 100vw)", background: "var(--color-surface-0)",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column",
        animation: "slideIn 0.2s ease",
      }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid var(--color-border)",
          display: "flex", gap: "12px", alignItems: "flex-start",
        }}>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--color-text-muted)", marginTop: "2px" }}>
            ×
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "6px" }}>
              <span style={{ fontFamily: "monospace", fontSize: "12px", color: "var(--color-brand-600)", fontWeight: 700 }}>
                {issue.displayIdFormatted}
              </span>
              <Badge tone={ISSUE_STATUS_TONES[issue.status] ?? "neutral"}>{issueStatusLabel(issue.status)}</Badge>
              <Chip label={issue.priority} color={PRIORITY_COLORS[issue.priority]} />
              <Chip label={TYPE_LABELS[issue.type] ?? issue.type} />
              {issue.module && <Chip label={moduleLabel(issue.module)} />}
            </div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "var(--color-text)", lineHeight: 1.3 }}>
              {issue.title}
            </h3>
            <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "4px" }}>
              Reported by {issue.reporterName ?? "Unknown"} · {timeAgo(issue.createdAt)}
            </div>
          </div>
        </div>

        {/* Triage: assignee, priority, module, status (engineers and admins) */}
        {canTriage ? (
          <IssueTriagePanel issue={issue} assignees={assignees} onChanged={refresh} onOpenIssue={onOpenIssue} />
        ) : (
          <div className="flex flex-col gap-2 border-b border-surface-200 px-5 py-3 empty:hidden">
            {issue.assigneeName && <p className="text-xs text-surface-600">Assigned to <span className="font-semibold">{issue.assigneeName}</span></p>}
            {issue.statusReason && <p className="text-xs text-surface-600"><span className="font-semibold">{issueStatusLabel(issue.status)} because:</span> {issue.statusReason}</p>}
            <IssueLinks issue={issue} onOpenIssue={onOpenIssue} />
          </div>
        )}

        {/* Reopen after a triage close */}
        {canContest && (
          <div className="flex flex-wrap items-start gap-3 border-b border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-5 py-3">
            <div className="min-w-[200px] flex-1">
              <p className="mb-1.5 text-[13px] font-semibold text-[var(--color-warning-fg)]">
                Marked {issueStatusLabel(issue.status)}. Still a problem? Reopen it with a note.
              </p>
              <Textarea size="sm" rows={2} value={retestNote} onChange={e => setRetestNote(e.target.value)} placeholder="Why it still needs work…" />
            </div>
            <AsyncButton variant="warning" onClick={() => doAction("reopen", { note: retestNote || "Still not fixed." })} disabled={!!actionLoading}>
              Reopen
            </AsyncButton>
          </div>
        )}

        {/* AI Improve */}
        {isReporter && !["resolved", "closed"].includes(issue.status) && !reviewAccepted && (
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--color-border)", background: "var(--color-surface-50)" }}>
            {!review ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button type="button" onClick={handleImprove} disabled={reviewLoading}
                  style={{ padding: "5px 12px", fontSize: "12px", fontWeight: 600, background: "var(--color-brand-50)", color: "var(--color-brand-700)", border: "1px solid var(--color-brand-200)", borderRadius: "var(--radius-sm)", cursor: "pointer" }}>
                  {reviewLoading ? "Improving…" : <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Sparkles size={14} /> Improve this report</span>}
                </button>
                {reviewError && <span style={{ fontSize: 12, color: "var(--color-error)" }}>AI improve unavailable — try again later</span>}
              </div>
            ) : (
              <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "12px 14px", fontSize: "13px" }}>
                <div style={{ fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}><Sparkles size={14} /> Suggested improvements</div>
                <div style={{ marginBottom: 6 }}><span style={{ color: "var(--color-text-muted)" }}>Title: </span>{review.suggested_title}</div>
                <div style={{ marginBottom: 8, whiteSpace: "pre-wrap", color: "var(--color-text)" }}>{review.rewritten_description}</div>
                {review.clarifying_questions.length > 0 && (
                  <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                    {review.clarifying_questions.map((q, i) => <li key={i} style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{q}</li>)}
                  </ul>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={acceptImprove} style={{ padding: "4px 12px", fontSize: "12px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600 }}>Apply</button>
                  <button onClick={() => setReview(null)} style={{ padding: "4px 12px", fontSize: "12px", background: "none", color: "var(--color-text-muted)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", cursor: "pointer" }}>Ignore</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Claim CTA */}
        {canClaim && (
          <div style={{
            background: "var(--color-info-bg)", border: "none", borderBottom: "1px solid var(--color-info-border)",
            padding: "14px 20px", display: "flex", gap: "10px", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
          }}>
            <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-info-fg)" }}>
              {issue.status === "reopened" ? "Reopened — claim it to pick this back up" : "Unassigned — claim it or pick an assignee above"}
            </div>
            <AsyncButton
              variant="info"
              onClick={() => doAction("claim")}
              disabled={!!actionLoading}
            >
              Claim
            </AsyncButton>
          </div>
        )}

        {/* Resolve CTA */}
        {canResolve && (
          <div style={{
            background: "var(--color-info-bg)", border: "none", borderBottom: "1px solid var(--color-info-border)",
            padding: "14px 20px", display: "flex", gap: "10px", alignItems: "flex-start", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: "200px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-info-fg)" }}>
                You're assigned to this issue
              </div>
              <textarea
                value={resolveSummary}
                onChange={e => setResolveSummary(e.target.value)}
                placeholder="What was fixed…"
                rows={2}
                style={{
                  width: "100%", padding: "6px 8px", fontSize: "12px", borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--color-info-border)", background: "var(--color-surface-0)", boxSizing: "border-box", resize: "none",
                }}
              />
              <textarea
                value={resolveTestSteps}
                onChange={e => setResolveTestSteps(e.target.value)}
                placeholder="How to verify…"
                rows={2}
                style={{
                  width: "100%", padding: "6px 8px", fontSize: "12px", borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--color-info-border)", background: "var(--color-surface-0)", boxSizing: "border-box", resize: "none",
                }}
              />
            </div>
            <AsyncButton
              variant="info"
              onClick={() => doAction("resolve", { summary: resolveSummary, testSteps: resolveTestSteps })}
              disabled={!!actionLoading || !resolveSummary.trim() || !resolveTestSteps.trim()}
            >
              Mark Resolved
            </AsyncButton>
          </div>
        )}

        {/* Retest CTA */}
        {canAct && (
          <div style={{
            background: "var(--color-warning-bg)", border: "none", borderBottom: "1px solid var(--color-warning-border)",
            padding: "14px 20px", display: "flex", gap: "10px", alignItems: "flex-start", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-warning-fg)", marginBottom: "6px" }}>
                Ready for your verification
              </div>
              <textarea
                value={retestNote}
                onChange={e => setRetestNote(e.target.value)}
                placeholder="Optional note…"
                rows={2}
                style={{
                  width: "100%", padding: "6px 8px", fontSize: "12px", borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--color-warning-border)", background: "var(--color-surface-0)", boxSizing: "border-box", resize: "none",
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <button
                onClick={() => doAction("close", { note: retestNote }).catch(() => {})}
                disabled={!!actionLoading}
                style={{
                  padding: "8px 16px", background: "var(--color-success)", color: "#fff", border: "none",
                  borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600, fontSize: "13px",
                }}>
                {actionLoading === "close" ? "…" : "Confirm Fixed"}
              </button>
              <AsyncButton
                variant="destructive"
                onClick={() => doAction("reopen", { note: retestNote || "Still not fixed." })}
                disabled={!!actionLoading}
              >
                Needs More Work
              </AsyncButton>
            </div>
          </div>
        )}

        {/* Thread */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <IssueAutoCaptured issue={issue} />
          {issue.entries.map(entry => {
            const meta = metaJson(entry);
            return (
              <div key={entry.id} style={{
                background: entry.entryType === "resolution" ? "var(--color-success-bg-subtle)" : "var(--color-surface-50)",
                border: `1px solid ${entry.entryType === "resolution" ? "var(--color-success-border)" : "var(--color-border)"}`,
                borderRadius: "var(--radius-md)", padding: "12px 14px",
              }}>
                <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "14px" }}>{ENTRY_ICONS[entry.entryType] ?? "•"}</span>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-text)" }}>
                    {entry.authorName ?? entry.author}
                  </span>
                  <span style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>{timeAgo(entry.createdAt)}</span>
                  <Chip label={entry.entryType.replace("_", " ")} />
                </div>
                {entry.body && (
                  <div style={{ fontSize: "13px", color: "var(--color-text)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                    {entry.body}
                  </div>
                )}
                {entry.entryType === "status_change" && meta["from"] && (
                  <div style={{ fontSize: "12px", color: "var(--color-text-muted)" }}>
                    {issueStatusLabel(meta["from"])} → {issueStatusLabel(meta["to"] ?? "")}
                  </div>
                )}
                {entry.entryType === "resolution" && meta["test_steps"] && (
                  <div style={{ marginTop: "8px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-success-fg)", marginBottom: "4px" }}>
                      Steps to verify
                    </div>
                    <pre style={{ margin: 0, fontSize: "12px", background: "var(--color-success-bg)", padding: "8px", borderRadius: "4px", whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                      {meta["test_steps"]}
                    </pre>
                    {meta["commit"] && (
                      <div style={{ fontSize: "11px", color: "var(--color-text-muted)", marginTop: "4px" }}>
                        Commit: <code style={{ fontFamily: "monospace" }}>{meta["commit"]}</code>
                      </div>
                    )}
                  </div>
                )}
                {entry.attachments && entry.attachments.map(att => (
                  <div key={att.id} style={{ marginTop: "8px" }}>
                    {att.mimeType?.startsWith("image/") ? (
                      attachUrls[att.id]
                        ? <a href={attachUrls[att.id]} target="_blank" rel="noreferrer" style={{ display: "inline-block" }}>
                            <img src={attachUrls[att.id]} alt={att.filename}
                              style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "4px", border: "1px solid var(--color-border)", cursor: "pointer" }} />
                          </a>
                        : <span style={{ fontSize: "12px", color: "var(--color-text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}><Loader2 size={12} className="animate-spin" /> {att.filename}</span>
                    ) : (
                      <a
                        href={attachUrls[att.id] ?? "#"}
                        target="_blank" rel="noreferrer"
                        onClick={e => { if (!attachUrls[att.id]) e.preventDefault(); }}
                        style={{ fontSize: "12px", color: attachUrls[att.id] ? "var(--color-brand-600)" : "var(--color-text-muted)", cursor: attachUrls[att.id] ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Paperclip size={12} /> {att.filename}{!attachUrls[att.id] && " (loading…)"}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Attachments not tied to entries */}
          {issue.attachments.filter(a => !a.entryId).length > 0 && (
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-text-muted)", marginBottom: "6px" }}>
                Attachments
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {issue.attachments.filter(a => !a.entryId).map(att => (
                  <div key={att.id}>
                    {att.mimeType?.startsWith("image/") ? (
                      attachUrls[att.id]
                        ? <a href={attachUrls[att.id]} target="_blank" rel="noreferrer">
                            <img src={attachUrls[att.id]} alt={att.filename}
                              style={{ width: "80px", height: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid var(--color-border)", cursor: "pointer", display: "block" }} />
                          </a>
                        : <div style={{ width: "80px", height: "60px", borderRadius: "4px", border: "1px solid var(--color-border)", background: "var(--color-surface-50)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "var(--color-text-muted)" }}><Loader2 size={14} className="animate-spin" /></div>
                    ) : (
                      <a
                        href={attachUrls[att.id] ?? "#"}
                        target="_blank" rel="noreferrer"
                        onClick={e => { if (!attachUrls[att.id]) e.preventDefault(); }}
                        style={{ fontSize: "12px", color: attachUrls[att.id] ? "var(--color-brand-600)" : "var(--color-text-muted)", cursor: attachUrls[att.id] ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Paperclip size={12} /> {att.filename}{!attachUrls[att.id] && " (loading…)"}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Comment box */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border)" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Add a comment…"
              rows={2}
              style={{
                flex: 1, padding: "8px 10px", fontSize: "13px", borderRadius: "var(--radius-sm)",
                border: "1px solid var(--color-border)", background: "var(--color-surface-0)",
                color: "var(--color-text)", resize: "none", fontFamily: "inherit",
              }}
            />
            <AsyncButton
              onClick={() => submitComment()}
              disabled={commentLoading || !commentText.trim()}
            >
              Send
            </AsyncButton>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Issue Credit Drill-Down ───────────────────────────────────────────────────

function IssueCreditDrillDown({ userId, kind, headerLabel, onClose, onSelectIssue }: {
  userId: string; kind: "fixer" | "closer"; headerLabel: string; onClose: () => void; onSelectIssue: (issueId: string) => void;
}) {
  const [items, setItems] = useState<CreditedIssueItem[] | null>(null);
  const endpoint = kind === "fixer" ? "confirmed-fixes" : "verified-closes";
  const emptyLabel = kind === "fixer" ? "No confirmed fixes yet" : "No verified closes yet";

  useEffect(() => {
    setItems(null);
    api.get<CreditedIssueItem[]>(`/issues/stats/${userId}/${endpoint}`)
      .then(r => setItems(r.data))
      .catch(err => logError("IssueCreditDrillDown.load", err));
  }, [userId, endpoint]);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 900 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 100vw)", zIndex: 901,
        background: "var(--color-surface-0)", boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid var(--color-border)",
        }}>
          <span style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-text)" }}>{headerLabel}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "var(--color-text-muted)" }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {items === null ? (
            <div style={{ padding: "24px", textAlign: "center", color: "var(--color-text-muted)", fontSize: "13px" }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: "var(--color-text-muted)", fontSize: "13px" }}>{emptyLabel}</div>
          ) : items.map(item => (
            <div
              key={item.id}
              onClick={() => { onClose(); onSelectIssue(item.id); }}
              style={{ padding: "12px 20px", borderBottom: "1px solid var(--color-border)", cursor: "pointer" }}
            >
              <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-text)" }}>
                {item.displayIdFormatted} — {item.title}
              </div>
              <div style={{ fontSize: "11px", color: "var(--color-text-muted)", marginTop: "3px" }}>
                Opened by {item.reporterName ?? "Unknown"} · {item.otherPartyRole} {item.otherPartyName}
                {item.closedAt && ` · ${new Date(item.closedAt).toLocaleDateString()}`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Issue Row ─────────────────────────────────────────────────────────────────

function IssueRow({ issue, onClick }: { issue: Issue; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "grid", gridTemplateColumns: "auto 1fr auto",
        alignItems: "center", gap: "12px", width: "100%", textAlign: "left",
        padding: "12px 16px", background: "var(--color-surface-0)",
        border: "none", borderBottom: "1px solid var(--color-border)",
        cursor: "pointer", transition: "background 0.1s",
      }}
      onMouseEnter={e => (e.currentTarget.style.background = "var(--color-surface-50)")}
      onMouseLeave={e => (e.currentTarget.style.background = "var(--color-surface-0)")}
    >
      <span style={{ fontFamily: "monospace", fontSize: "11px", fontWeight: 700, color: "var(--color-brand-600)", whiteSpace: "nowrap" }}>
        {issue.displayIdFormatted}
      </span>
      <div>
        <div style={{ fontWeight: 500, fontSize: "13px", color: "var(--color-text)", marginBottom: "4px" }}>
          {issue.title}
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <Badge tone={ISSUE_STATUS_TONES[issue.status] ?? "neutral"}>{issueStatusLabel(issue.status)}</Badge>
          <Chip label={issue.priority} color={PRIORITY_COLORS[issue.priority]} />
          {issue.module && <Chip label={moduleLabel(issue.module)} />}
          <Badge tone="outline">{issue.assigneeName ? `Assigned: ${issue.assigneeName}` : "Unassigned"}</Badge>
          <span style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
            {issue.reporterName ?? "Unknown"} · {timeAgo(issue.createdAt)}
          </span>
        </div>
      </div>
      <span style={{ fontSize: "11px", color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
        {TYPE_LABELS[issue.type] ?? issue.type}
      </span>
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type Tab = "all" | "mine" | "assigned" | "impact" | "leaderboard";
const TAB_LABELS: Record<Tab, string> = {
  all: "All Issues", mine: "My Issues", assigned: "Assigned to Me", impact: "My Impact", leaderboard: "Leaderboard",
};

export default function IssueTrackerPage() {
  const { accessToken } = useAuth();
  const [searchParams] = useSearchParams();
  const issuePanel = useIssuePanel();
  const currentUser = accessToken ? decodeUser(accessToken) : { sub: "", email: "", role: "" };
  // Admin cards link here; staff without admin access have no Admin page to return to.
  const fromAdmin = HR_ROLES.has(currentUser.role);
  usePageBack(fromAdmin ? "/admin" : "/dashboard", fromAdmin ? "Back to Admin" : "Back to Dashboard");
  const PREFS_KEY = `hrms_prefs_issues_${currentUser.sub}`;

  const [tab, setTab] = useState<Tab>(() => {
    try {
      const d = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).tab;
      return typeof d === "string" && d in TAB_LABELS ? d as Tab : "all";
    } catch { return "all"; }
  });
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState<string[]>(() => {
    try {
      const raw = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).filterStatuses;
      return Array.isArray(raw) ? raw as string[] : [];
    } catch { return []; }
  });
  const [filterModules, setFilterModules] = useState<string[]>(() => {
    try {
      const raw = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).filterModules;
      return Array.isArray(raw) ? raw as string[] : [];
    } catch { return []; }
  });
  const [filterPriorities, setFilterPriorities] = useState<string[]>(() => {
    try {
      const raw = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).filterPriorities;
      return Array.isArray(raw) ? raw as string[] : [];
    } catch { return []; }
  });
  const [filterAssignee, setFilterAssignee] = useState<string>(() => {
    try { return String((JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).filterAssignee ?? ""); }
    catch { return ""; }
  });
  const [canTriage, setCanTriage] = useState(false);
  const [assignees, setAssignees] = useState<IssueAssignee[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [sortBy, setSortBy] = useState<string>(() => {
    try { return String((JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).sortBy ?? "createdAt"); }
    catch { return "createdAt"; }
  });
  const [sortDir, setSortDir] = useState<"asc" | "desc">(() => {
    try {
      const d = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).sortDir;
      return d === "asc" || d === "desc" ? d : "desc";
    } catch { return "desc"; }
  });
  const [showStatusPop, setShowStatusPop] = useState(false);
  const [showModulePop, setShowModulePop] = useState(false);
  const [showPriorityPop, setShowPriorityPop] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [impact, setImpact] = useState<MyImpact | null>(null);
  const [leaderboard, setLeaderboard] = useState<ReporterStats[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<{ userId: string; kind: "fixer" | "closer"; headerLabel: string } | null>(null);

  const highlightId = searchParams.get("highlight");

  useEffect(() => {
    api.get<{ canTriage: boolean }>("/issues/me/permissions").then(r => setCanTriage(r.data.canTriage)).catch(err => logError("IssueTrackerPage.loadPermissions", err));
    api.get<IssueAssignee[]>("/issues/assignees").then(r => setAssignees(r.data)).catch(err => logError("IssueTrackerPage.loadAssignees", err));
  }, []);
  const loadIssuesSeq = useRef(0);

  useEffect(() => {
    if (highlightId) {
      // Find issue by highlight param (issueId)
      setSelectedIssueId(highlightId);
    }
  }, [highlightId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadIssues = useCallback(async () => {
    const seq = ++loadIssuesSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatuses.length) params.set("status", filterStatuses.join(","));
      if (filterModules.length) params.set("module", filterModules.join(","));
      if (filterPriorities.length) params.set("priority", filterPriorities.join(","));
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (sortBy !== "createdAt" || sortDir !== "desc") { params.set("sortBy", sortBy); params.set("sortDir", sortDir); }
      if (tab === "mine") params.set("reporter", currentUser.sub);
      if (tab === "assigned") params.set("assignee", "me");
      else if (filterAssignee) params.set("assignee", filterAssignee);
      const { data } = await api.get<Issue[]>(`/issues?${params}`);
      if (seq === loadIssuesSeq.current) setIssues(data);
    } catch (err) { logError("IssueTrackerPage.loadIssues", err); }
    if (seq === loadIssuesSeq.current) setLoading(false);
  }, [filterStatuses, filterModules, filterPriorities, filterAssignee, debouncedSearch, sortBy, sortDir, tab, currentUser.sub]);

  useEffect(() => {
    if (tab === "all" || tab === "mine" || tab === "assigned") loadIssues();
    if (tab === "impact") {
      api.get<MyImpact>("/issues/stats/my-impact").then(r => setImpact(r.data)).catch((err) => logError("IssueTrackerPage.loadImpactStats", err));
    }
    if (tab === "leaderboard") {
      api.get<ReporterStats[]>("/issues/stats/leaderboard").then(r => setLeaderboard(r.data)).catch((err) => logError("IssueTrackerPage.loadLeaderboard", err));
    }
  }, [tab, loadIssues]);

  useEffect(() => {
    const handler = () => { void loadIssues(); };
    window.addEventListener("hrms:issue:submitted", handler);
    return () => window.removeEventListener("hrms:issue:submitted", handler);
  }, [loadIssues]);

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ tab, filterStatuses, filterModules, filterPriorities, filterAssignee, sortBy, sortDir }));
    } catch { /* private browsing */ }
  }, [PREFS_KEY, tab, filterStatuses, filterModules, filterPriorities, filterAssignee, sortBy, sortDir]);

  function handleUpdated() {
    loadIssues();
  }

  const myPendingRetest = issues.filter(i => i.reporter === currentUser.sub && ["resolved", "retesting"].includes(i.status));
  const [pageSize, setPageSize] = useState(25);
  const issueFilterKey = [tab, filterStatuses, filterModules, filterPriorities, filterAssignee, debouncedSearch, sortBy, sortDir, pageSize].join("|");
  const { pageItems: displayedIssues, meta: issuesMeta, setPage: setIssuesPage } = usePaged(issues, issueFilterKey, pageSize);
  const { pageItems: leaderboardRows, meta: leaderboardMeta, offset: leaderboardOffset, setPage: setLeaderboardPage } = usePaged(leaderboard, `lb|${pageSize}`, pageSize);

  function toggleSort(field: string) {
    if (sortBy === field) { setSortDir(d => d === "desc" ? "asc" : "desc"); }
    else { setSortBy(field); setSortDir("desc"); }
  }
  function toggleFilter(arr: string[], val: string, set: (v: string[]) => void) {
    set(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  }

  const allBadges = Object.keys(BADGE_LABELS);
  const earnedBadges: string[] = impact?.badgesJson ? JSON.parse(impact.badgesJson) as string[] : [];

  const inputStyle: React.CSSProperties = {
    padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)",
    background: "var(--color-surface-0)", color: "var(--color-text)", fontSize: "12px",
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "24px", right: "24px", zIndex: 9000,
          background: "var(--color-success)", color: "#fff", padding: "12px 20px", borderRadius: "var(--radius-md)",
          fontSize: "13px", fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "var(--color-text)" }}>
            Issue Tracker
          </h1>
          <div style={{ fontSize: "13px", color: "var(--color-text-muted)", marginTop: "2px" }}>
            Report bugs and track fixes
          </div>
        </div>
        <button
          onClick={() => issuePanel.open()}
          title="Add New Issue"
          style={{
            width: 40, height: 40, borderRadius: "50%",
            background: "var(--color-brand-600)", color: "#fff",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22,
          }}>
          <span>+</span>
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "2px", marginBottom: "20px", borderBottom: "2px solid var(--color-border)" }}>
        {(Object.keys(TAB_LABELS) as Tab[]).filter(t => t !== "assigned" || canTriage).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "8px 18px", background: "none", border: "none",
              borderBottom: tab === t ? "2px solid var(--color-brand-600)" : "2px solid transparent",
              marginBottom: "-2px", cursor: "pointer", fontWeight: tab === t ? 700 : 500,
              fontSize: "13px",
              color: tab === t ? "var(--color-brand-600)" : "var(--color-text-muted)",
            }}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* All Issues / My Issues */}
      {(tab === "all" || tab === "mine" || tab === "assigned") && (
        <>
          {/* Search + Filters + Sort */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px", alignItems: "center" }}>
            {/* Search */}
            <input
              style={{ ...inputStyle, width: 200 }}
              placeholder="Search by title or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") setDebouncedSearch(search); }}
            />

            {/* Status multi-select */}
            <div className="relative">
              <DropdownButton expanded={showStatusPop} onClick={() => { setShowStatusPop(p => !p); setShowModulePop(false); setShowPriorityPop(false); }}>
                {filterStatuses.length ? `Status (${filterStatuses.length})` : "All Statuses"}
              </DropdownButton>
              {showStatusPop && (
                <DropdownPanel className="min-w-[160px]" onClose={() => setShowStatusPop(false)}>
                  <MenuList className="p-2">
                    {ISSUE_STATUS_ORDER.map(s => (
                      <DropdownCheckboxRow key={s} checked={filterStatuses.includes(s)} onChange={() => toggleFilter(filterStatuses, s, setFilterStatuses)}>
                        {issueStatusLabel(s)}
                      </DropdownCheckboxRow>
                    ))}
                    {filterStatuses.length > 0 && <button onClick={() => setFilterStatuses([])} className="mt-1 cursor-pointer border-none bg-transparent p-0 text-[11px] text-brand-600">Clear</button>}
                  </MenuList>
                </DropdownPanel>
              )}
            </div>

            {/* Module multi-select */}
            <div className="relative">
              <DropdownButton expanded={showModulePop} onClick={() => { setShowModulePop(p => !p); setShowStatusPop(false); setShowPriorityPop(false); }}>
                {filterModules.length ? `Module (${filterModules.length})` : "All Modules"}
              </DropdownButton>
              {showModulePop && (
                <DropdownPanel className="max-h-80 min-w-[200px] overflow-auto">
                  <MenuList className="p-2">
                    {Object.entries(MODULE_LABELS).map(([m, label]) => (
                      <DropdownCheckboxRow key={m} checked={filterModules.includes(m)} onChange={() => toggleFilter(filterModules, m, setFilterModules)}>
                        {label}
                      </DropdownCheckboxRow>
                    ))}
                    {filterModules.length > 0 && <button onClick={() => setFilterModules([])} className="mt-1 cursor-pointer border-none bg-transparent p-0 text-[11px] text-brand-600">Clear</button>}
                  </MenuList>
                </DropdownPanel>
              )}
            </div>

            {/* Priority multi-select */}
            <div className="relative">
              <DropdownButton expanded={showPriorityPop} onClick={() => { setShowPriorityPop(p => !p); setShowStatusPop(false); setShowModulePop(false); }}>
                {filterPriorities.length ? `Priority (${filterPriorities.length})` : "All Priorities"}
              </DropdownButton>
              {showPriorityPop && (
                <DropdownPanel className="min-w-[140px]" onClose={() => setShowPriorityPop(false)}>
                  <MenuList className="p-2">
                    {["low", "medium", "high", "critical"].map(p => (
                      <DropdownCheckboxRow key={p} checked={filterPriorities.includes(p)} onChange={() => toggleFilter(filterPriorities, p, setFilterPriorities)}>
                        {p}
                      </DropdownCheckboxRow>
                    ))}
                    {filterPriorities.length > 0 && <button onClick={() => setFilterPriorities([])} className="mt-1 cursor-pointer border-none bg-transparent p-0 text-[11px] text-brand-600">Clear</button>}
                  </MenuList>
                </DropdownPanel>
              )}
            </div>

            {/* Assignee */}
            {tab !== "assigned" && (
              <SelectField className="w-auto" specular value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} aria-label="Assignee">
                <option value="">All assignees</option>
                <option value="me">Assigned to me</option>
                <option value="unassigned">Unassigned</option>
                {assignees.map(a => <option key={a.userId} value={a.userId}>{a.name}</option>)}
              </SelectField>
            )}

            {/* Sort */}
            <SelectField className="w-auto" specular value={`${sortBy}:${sortDir}`}
              onChange={e => { const [f = "createdAt", d = "desc"] = e.target.value.split(":"); setSortBy(f); setSortDir(d as "asc" | "desc"); }}>
              <option value="createdAt:desc">Newest first</option>
              <option value="createdAt:asc">Oldest first</option>
              <option value="priority:desc">Priority (high→low)</option>
              <option value="priority:asc">Priority (low→high)</option>
              <option value="displayId:desc">ID (newest)</option>
              <option value="displayId:asc">ID (oldest)</option>
            </SelectField>

            <button onClick={loadIssues} style={{ ...inputStyle, cursor: "pointer", background: "var(--color-surface-100)" }}>
              Apply
            </button>

            {(filterStatuses.length > 0 || filterModules.length > 0 || filterPriorities.length > 0 || filterAssignee || sortBy !== "createdAt" || sortDir !== "desc") && (
              <button onClick={() => {
                try { localStorage.removeItem(PREFS_KEY); } catch { /* */ }
                setFilterStatuses([]); setFilterModules([]); setFilterPriorities([]); setFilterAssignee("");
                setSortBy("createdAt"); setSortDir("desc");
              }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--color-brand-600)", fontWeight: 600, padding: "4px 0" }}>
                Reset filters
              </button>
            )}
          </div>

          {/* Pending retest banner (My Issues tab) */}
          {tab === "mine" && myPendingRetest.length > 0 && (
            <div style={{
              background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: "var(--radius-md)",
              padding: "12px 16px", marginBottom: "16px",
            }}>
              <div style={{ fontWeight: 700, fontSize: "13px", color: "var(--color-warning-fg)", marginBottom: "6px" }}>
                Awaiting Your Retest ({myPendingRetest.length})
              </div>
              {myPendingRetest.map(i => (
                <button key={i.id} onClick={() => setSelectedIssueId(i.id)}
                  style={{
                    display: "flex", gap: "8px", alignItems: "center", background: "none",
                    border: "none", cursor: "pointer", color: "var(--color-warning-fg)", fontSize: "13px", padding: "4px 0",
                  }}>
                  <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{i.displayIdFormatted}</span>
                  {i.title}
                </button>
              ))}
            </div>
          )}

          {/* Issue list */}
          <div style={{
            background: "var(--color-surface-0)", border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)", overflow: "hidden",
          }}>
            {loading ? (
              <div style={{ padding: "32px", textAlign: "center", color: "var(--color-text-muted)" }}>Loading…</div>
            ) : displayedIssues.length === 0 ? (
              <div style={{ padding: "32px", textAlign: "center", color: "var(--color-text-muted)" }}>
                No issues found
              </div>
            ) : displayedIssues.map(issue => (
              <IssueRow key={issue.id} issue={issue} onClick={() => setSelectedIssueId(issue.id)} />
            ))}
          </div>
          <Pagination meta={issuesMeta} onPageChange={setIssuesPage} onPageSizeChange={setPageSize} />
        </>
      )}

      {/* My Impact */}
      {tab === "impact" && (
        <div>
          {!impact ? (
            <div style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>Loading…</div>
          ) : (
            <>
              <div style={{
                background: "var(--color-surface-0)", border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)", padding: "24px", marginBottom: "20px",
              }}>
                <div style={{ fontSize: "28px", fontWeight: 800, color: "var(--color-text)", marginBottom: "4px" }}>
                  You've helped fix {impact.confirmedFixedCount} issue{impact.confirmedFixedCount === 1 ? "" : "s"}
                </div>
                {impact.avgTurnaroundHours !== null && (
                  <div style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                    Average turnaround: {impact.avgTurnaroundHours}h
                  </div>
                )}
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "24px",
              }}>
                {[
                  { label: "Submitted", value: impact.submittedCount, kind: null },
                  { label: "Open", value: impact.openCount, kind: null },
                  { label: "Fixed & Confirmed", value: impact.confirmedFixedCount, kind: "fixer" as const },
                  { label: "Verified & Closed", value: impact.verifiedClosedCount, kind: "closer" as const },
                  { label: "Awaiting Retest", value: impact.pendingRetest, kind: null },
                ].map(({ label, value, kind }) => (
                  <div
                    key={label}
                    onClick={kind && value > 0 ? () => setDrillDown({ userId: currentUser.sub, kind, headerLabel: `Your ${label.toLowerCase()}` }) : undefined}
                    style={{
                      background: "var(--color-surface-0)", border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)", padding: "16px",
                      cursor: kind && value > 0 ? "pointer" : "default",
                    }}
                  >
                    <div style={{ fontSize: "26px", fontWeight: 800, color: "var(--color-text)", fontVariantNumeric: "tabular-nums" }}>
                      {value}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "2px" }}>{label}</div>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-text)", marginBottom: "12px" }}>Badges</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {allBadges.map(badge => {
                    const earned = earnedBadges.includes(badge);
                    return (
                      <div key={badge} style={{
                        padding: "10px 16px", borderRadius: "var(--radius-md)",
                        border: `2px solid ${earned ? "var(--color-brand-400)" : "var(--color-border)"}`,
                        background: earned ? "var(--color-brand-50)" : "var(--color-surface-50)",
                        opacity: earned ? 1 : 0.45, textAlign: "center",
                      }}>
                        <div style={{ fontSize: "22px", marginBottom: "4px", display: "flex", justifyContent: "center" }}>
                          {earned ? <Trophy size={20} color="var(--color-brand-600)" /> : <Lock size={20} />}
                        </div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-text)" }}>
                          {BADGE_LABELS[badge]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Leaderboard */}
      {tab === "leaderboard" && (
        <div>
          <div style={{ fontSize: "13px", color: "var(--color-text-muted)", marginBottom: "16px" }}>
            Top contributors this month — ranked by confirmed & closed issues only.
          </div>
          <div style={{
            background: "var(--color-surface-0)", border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)", overflow: "hidden",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)", background: "var(--color-surface-50)" }}>
                  {["Rank", "Contributor", "Submitted", "Confirmed Fixes", "Verified & Closed", "Score", "Badges"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "var(--color-text-muted)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaderboard.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: "32px", textAlign: "center", color: "var(--color-text-muted)" }}>
                      No data yet this month
                    </td>
                  </tr>
                ) : leaderboardRows.map((row, idx) => {
                  const i = leaderboardOffset + idx;
                  const badges: string[] = row.badgesJson ? JSON.parse(row.badgesJson) as string[] : [];
                  return (
                    <tr key={row.reporter} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "10px 16px", fontWeight: 700, color: i === 0 ? "var(--color-warning-accent)" : "var(--color-text)" }}>
                        {i === 0 ? <Trophy size={16} color="var(--color-warning-accent)" /> : i === 1 ? <Medal size={16} color="var(--color-text-400)" /> : i === 2 ? <Medal size={16} color="var(--color-warning-fg-strong)" /> : `#${i + 1}`}
                      </td>
                      <td style={{ padding: "10px 16px", color: "var(--color-text)" }}>
                        {row.displayName}
                      </td>
                      <td style={{ padding: "10px 16px", fontVariantNumeric: "tabular-nums" }}>
                        {row.submittedCount}
                      </td>
                      <td
                        onClick={row.confirmedFixedCount > 0 ? () => setDrillDown({ userId: row.reporter, kind: "fixer", headerLabel: `${row.displayName}'s confirmed fixes` }) : undefined}
                        style={{
                          padding: "10px 16px", fontVariantNumeric: "tabular-nums", fontWeight: 600,
                          cursor: row.confirmedFixedCount > 0 ? "pointer" : "default",
                          textDecoration: row.confirmedFixedCount > 0 ? "underline" : "none",
                          textDecorationColor: "var(--color-border)",
                        }}
                      >
                        {row.confirmedFixedCount}
                      </td>
                      <td
                        onClick={row.verifiedClosedCount > 0 ? () => setDrillDown({ userId: row.reporter, kind: "closer", headerLabel: `${row.displayName}'s verified & closed` }) : undefined}
                        style={{
                          padding: "10px 16px", fontVariantNumeric: "tabular-nums", fontWeight: 600,
                          cursor: row.verifiedClosedCount > 0 ? "pointer" : "default",
                          textDecoration: row.verifiedClosedCount > 0 ? "underline" : "none",
                          textDecorationColor: "var(--color-border)",
                        }}
                      >
                        {row.verifiedClosedCount}
                      </td>
                      <td style={{ padding: "10px 16px", fontVariantNumeric: "tabular-nums" }}>
                        {row.weightedScore}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                          {badges.map(b => (
                            <Chip key={b} label={BADGE_LABELS[b] ?? b} color="var(--color-accent-accent)" />
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination meta={leaderboardMeta} onPageChange={setLeaderboardPage} onPageSizeChange={setPageSize} />
        </div>
      )}

      {/* Detail drawer */}
      {selectedIssueId && (
        <DetailDrawer
          issueId={selectedIssueId}
          currentUserId={currentUser.sub}
          canTriage={canTriage}
          assignees={assignees}
          onClose={() => setSelectedIssueId(null)}
          onUpdated={handleUpdated}
          onOpenIssue={setSelectedIssueId}
        />
      )}

      {/* Issue credit drill-down */}
      {drillDown && (
        <IssueCreditDrillDown
          userId={drillDown.userId}
          kind={drillDown.kind}
          headerLabel={drillDown.headerLabel}
          onClose={() => setDrillDown(null)}
          onSelectIssue={(issueId) => setSelectedIssueId(issueId)}
        />
      )}

    </div>
  );
}
